import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AccountCampaignSelector as RawSelector } from '@bingads-webui-campaign/account-campaign-selector';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { Button } from '@bingads-webui-react/primitive';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { getMasStyle } from './styles';
import { getScopeFilterLevel, ScopeFilterLevel } from '../../helpers/account-selector-helper';

function MultipleAccountSelector(props) {
  const {
    getDefaultSelected, onDismiss, onSelectedChange, reportSchema, searchHistorySelectorOptions: searchHistorySelectorOptionsFromProps,
  } = props;

  const classes = useDefaultStyles(getMasStyle);
  const { scope } = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);
  const i18n = useContext(I18nContext);

  const {
    IsCustomerHierarchyEnabled,
    IsReportingUINewDesignEnabled,
    IsInternalUser,
  } = serverContext.Permissions;

  const defaultSelected = getDefaultSelected();

  const [selected, setSelected] = useState(defaultSelected);
  const [searchHistorySelectorOptions, setSearchHistorySelectorOptions] = useState(searchHistorySelectorOptionsFromProps);

  const [loaded, setLoaded] = useState(false);

  function onApply() {
    if (_.isEqual(selected, defaultSelected)) {
      onDismiss();
    } else {
      onSelectedChange(selected, {
        searchHistorySelectorOptions,
      });
    }
  }

  function onDismissCallback() {
    // reset selected state
    setSelected(defaultSelected);
    onDismiss();
  }

  function onChange(newSelected, addtionalInfo) {
    // called whenever selected account is changed in account-campaign-selector
    // need to cache the newly selected accounts
    // however we only propogate the change to parent when Apply button is clicked
    setSelected(newSelected);
    setSearchHistorySelectorOptions(addtionalInfo && addtionalInfo.searchHistorySelectorOptions);
  }

  function getI18NString(name) {
    return i18n.getString(_TL_(name));
  }

  function finishedLoading() {
    setLoaded(true);
  }

  const scopeFilterLevel = getScopeFilterLevel(reportSchema.ScopeFilterLevel);

  const featurePilots = {
    IsCustomerHierarchyEnabled,
    IsReportingUINewDesignEnabled,
  };

  return (
    <UiBlocker
      blocking={!loaded}
      loadingText={i18n.getString(_TL_('Loading ...'))}
    >
      <div
        className={`${classes.multipleAccountSelector} account-selector-popup`}
      >
        <RawSelector
          i18n={i18n}
          odataURI={serverContext.ServicesEndpoints.CampaignAPI}
          ccBaseUrl={serverContext.ServicesEndpoints.ClientCenterUIAPI}
          ccToken={serverContext.Token}
          customerId={scope.values.cid}
          userId={scope.values.uid}
          featurePilots={featurePilots}
          onSelectedChange={onChange}
          finishedLoading={finishedLoading}
          accountId={scope.values.aid}
          isReportingUIPhaseNext
          forceAccountScope={scopeFilterLevel === ScopeFilterLevel.Account}
          scopeFilterLevel={scopeFilterLevel}
          isInternalReport={IsInternalUser}
          hideRadioButtons
          allSelected={false}
          isCreateSimilar
          defaultSelected={defaultSelected}
          searchHistorySelectorOptions={searchHistorySelectorOptions}
        />
        <div className="actions">
          <Button className="apply btn-primary" onClick={onApply}>{getI18NString('Save')}</Button>
          <Button className="cancel btn-default" onClick={onDismissCallback}>{getI18NString('Cancel')}</Button>
        </div>
      </div>
    </UiBlocker>
  );
}

MultipleAccountSelector.propTypes = {
  onSelectedChange: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  getDefaultSelected: PropTypes.func.isRequired,
  reportSchema: PropTypes.shape({}).isRequired,
  searchHistorySelectorOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export { MultipleAccountSelector };
